import { useEffect, useRef, useState } from "react";
import { Field, Stamp } from "@/components/Doc";
import { clientApi } from "@/lib/api";

export const StepShell = ({ n, title, children }) => (
  <div className="settle max-w-3xl mx-auto" data-testid={`authorize-step-${n}`}>
    <div className="flex justify-between items-end border-b border-rule pb-2">
      <span className="t-label text-olive" data-testid="authorize-progress">Step {String(n).padStart(2, "0")} of 06</span>
      <span className="t-label text-rule">Form LH-40</span>
    </div>
    <h2 className="t-head text-3xl sm:text-4xl mt-8">{title}</h2>
    <div className="mt-8">{children}</div>
  </div>
);

const Check = ({ checked, onChange, testId, children }) => (
  <label className="flex gap-3 items-start cursor-pointer">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1.5 w-4 h-4 accent-[#1A1A18]" data-testid={testId} />
    <span className="t-body text-lg leading-snug">{children}</span>
  </label>
);

const Err = ({ msg }) => msg ? <p className="t-body text-base text-stamp mt-3" data-testid="step-error">{msg}</p> : null;

export const Step1 = ({ state, onDone }) => {
  const [agreement, setAgreement] = useState("");
  const a = state.authorization || {};
  const [f, setF] = useState({ signatoryName: a.signatoryName || "", signatoryTitle: a.signatoryTitle || "", authorityAffirmed: false, typedSignature: "" });
  const [err, setErr] = useState("");
  const [note, setNote] = useState(null);
  useEffect(() => { clientApi().get("/authorize/agreement").then((r) => setAgreement(r.data.text)); }, []);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { const { data } = await clientApi().post("/authorize/step/1", f); if (data.note) { setNote(data.note); setTimeout(onDone, 2200); } else onDone(); }
    catch (x) { setErr(x.response?.data?.detail || x.message); }
  };
  return (
    <StepShell n={1} title="Authorization & scope agreement">
      <div className="relative bg-paper border border-carbon p-6 sm:p-8 max-h-[380px] overflow-y-auto shadow-[4px_4px_0_0_#D9C9A8]" data-testid="agreement-text">
        <div className="absolute top-3 right-3"><Stamp color="red" rotate={5} size="sm">Draft — Pending Counsel Review</Stamp></div>
        <pre className="t-body text-base whitespace-pre-wrap leading-[1.6] mt-6">{agreement}</pre>
      </div>
      <form onSubmit={submit} className="mt-8 flex flex-col gap-6">
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="Signatory full name" name="signatoryName" value={f.signatoryName} onChange={(e) => setF({ ...f, signatoryName: e.target.value })} required testId="sig-name" />
          <Field label="Signatory title" name="signatoryTitle" value={f.signatoryTitle} onChange={(e) => setF({ ...f, signatoryTitle: e.target.value })} testId="sig-title" />
        </div>
        <Check checked={f.authorityAffirmed} onChange={(v) => setF({ ...f, authorityAffirmed: v })} testId="sig-authority">
          I have authority to bind {state.client.legalName} to this agreement and to authorize testing of the locations and contact points listed.
        </Check>
        <Field label="Type your full name to sign" name="typedSignature" value={f.typedSignature} onChange={(e) => setF({ ...f, typedSignature: e.target.value })} required testId="sig-typed" />
        <p className="t-label text-rule">Signature, timestamp and IP address are stored with the instrument.</p>
        {note && <p className="t-body text-base text-olive" data-testid="junior-note">{note}</p>}
        <Err msg={err} />
        <button className="btn btn-stamp self-start" disabled={!f.authorityAffirmed} data-testid="step1-submit">Sign and Continue</button>
      </form>
    </StepShell>
  );
};

const emptyRow = { locationName: "", channel: "voice", value: "", department: "", businessHours: "" };

export const Step2 = ({ state, onDone }) => {
  const [rows, setRows] = useState(state.targets.length ? state.targets.map((t) => ({ locationName: state.locations.find((l) => l.id === t.locationId || l._id === t.locationId)?.name || "", channel: t.channel, value: t.value, department: t.department, businessHours: t.businessHours || "" })) : [{ ...emptyRow }]);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const upd = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const importCsv = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const lines = reader.result.split(/\r?\n/).filter(Boolean);
      const parsed = lines.filter((l) => !/^location/i.test(l)).map((l) => { const c = l.split(",").map((s) => s.trim()); return { locationName: c[0] || "", channel: (c[1] || "voice").toLowerCase(), value: c[2] || "", department: c[3] || "", businessHours: c[4] || "" }; });
      if (parsed.length) setRows(parsed);
    };
    reader.readAsText(file);
  };
  const submit = async () => {
    setErr("");
    try { const { data } = await clientApi().post("/authorize/step/2", { targets: rows }); setResult(data); if (data.accepted) onDone(); }
    catch (x) { setErr(x.response?.data?.detail || x.message); }
  };
  const rejected = result ? result.targets.filter((t) => t.screenResult === "rejected") : [];
  return (
    <StepShell n={2} title="Target list">
      <p className="t-body text-lg">Every location and contact point we may test. Phone numbers are screened for line type; we only test business lines.</p>
      <div className="mt-4 flex flex-col sm:flex-row gap-4 sm:items-center">
        <label className="btn cursor-pointer">Upload CSV<input type="file" accept=".csv,text/csv" onChange={importCsv} className="hidden" data-testid="csv-input" /></label>
        <span className="t-label text-rule">Columns: location name, contact type (voice | email | sms), number or address, department, business hours</span>
      </div>
      <div className="mt-6 flex flex-col gap-4">
        {rows.map((r, i) => {
          const rej = rejected.find((t) => t.value === r.value.trim());
          return (
            <div key={i} className={`border ${rej ? "border-stamp" : "border-rule"} bg-manila/40 p-4 grid sm:grid-cols-12 gap-3`} data-testid={`target-row-${i}`}>
              <input className="doc-field sm:col-span-3" placeholder="Location name" value={r.locationName} onChange={(e) => upd(i, "locationName", e.target.value)} data-testid={`t-loc-${i}`} />
              <select className="doc-field sm:col-span-2" value={r.channel} onChange={(e) => upd(i, "channel", e.target.value)} data-testid={`t-channel-${i}`}><option value="voice">voice</option><option value="email">email</option><option value="sms">sms</option></select>
              <input className="doc-field sm:col-span-3" placeholder="Number or address" value={r.value} onChange={(e) => upd(i, "value", e.target.value)} data-testid={`t-value-${i}`} />
              <input className="doc-field sm:col-span-2" placeholder="Department" value={r.department} onChange={(e) => upd(i, "department", e.target.value)} data-testid={`t-dept-${i}`} />
              <div className="sm:col-span-2 flex gap-2 items-end">
                <input className="doc-field" placeholder="Hours" value={r.businessHours} onChange={(e) => upd(i, "businessHours", e.target.value)} data-testid={`t-hours-${i}`} />
                <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="t-label text-olive hover:text-stamp pb-2" data-testid={`t-remove-${i}`}>Remove</button>
              </div>
              {rej && <p className="sm:col-span-12 t-body text-base text-stamp leading-snug" data-testid={`t-rejected-${i}`}>{rej.message}</p>}
            </div>
          );
        })}
      </div>
      <div className="mt-6 flex flex-col sm:flex-row gap-4">
        <button type="button" onClick={() => setRows([...rows, { ...emptyRow }])} className="btn" data-testid="add-target-btn">Add a contact point</button>
        <button type="button" onClick={submit} className="btn btn-stamp" disabled={!rows.length} data-testid="step2-submit">Screen and Continue</button>
      </div>
      {result && !result.accepted && <p className="t-body text-base text-stamp mt-4" data-testid="step2-blocked">The list cannot be submitted while a rejected number remains. Replace or remove it, then screen again.</p>}
      <p className="t-label text-rule mt-4">Line-type screening is currently a MOCK (Twilio Lookup pending): numbers ending in an odd digit are treated as mobile.</p>
      <Err msg={err} />
    </StepShell>
  );
};

export const Step3 = ({ state, onDone }) => {
  const [attested, setAttested] = useState(false);
  const [text, setText] = useState(state.authorization?.thirdPartyDisclosure || "");
  const [err, setErr] = useState("");
  const submit = async () => { setErr(""); try { await clientApi().post("/authorize/step/3", { attested, thirdPartyDisclosure: text }); onDone(); } catch (x) { setErr(x.response?.data?.detail || x.message); } };
  return (
    <StepShell n={3} title="Attestation of control">
      <Check checked={attested} onChange={setAttested} testId="attest-check">
        {state.client.legalName} owns or directly controls every number and address listed, and no listed contact point is operated by a third party — a shared call center, an outsourced BDC, an answering service, or a co-tenant.
      </Check>
      <div className="mt-8">
        <Field label="List any contact point operated by a third party" name="tp" as="textarea" value={text} onChange={(e) => setText(e.target.value)} placeholder="Leave blank if none." testId="third-party-text" />
        <p className="t-label text-rule mt-2">Any entry here routes the account for manual review before activation.</p>
      </div>
      <Err msg={err} />
      <button onClick={submit} className="btn btn-stamp mt-8" disabled={!attested} data-testid="step3-submit">Attest and Continue</button>
    </StepShell>
  );
};

const TEMPLATE = (co) => `${co} periodically evaluates service quality and security practices through simulated customer contacts and simulated security scenarios, conducted by an authorized third party. These interactions may be recorded. Results are reported at team level; no individual employee is identified in any report.`;

export const Step4 = ({ state, onDone, onBack }) => {
  const [confirmed, setConfirmed] = useState(false);
  const [date, setDate] = useState(state.authorization?.workforceNoticeDate || "");
  const [reveal, setReveal] = useState(false);
  const [err, setErr] = useState("");
  const timer = useRef(null);
  useEffect(() => { timer.current = setTimeout(() => setReveal(true), 20000); return () => clearTimeout(timer.current); }, []);
  const submit = async () => { setErr(""); try { await clientApi().post("/authorize/step/4", { confirmed, workforceNoticeDate: date }); onDone(); } catch (x) { setErr(x.response?.data?.detail || x.message); } };
  return (
    <StepShell n={4} title="Workforce notice">
      <p className="t-body text-lg">Before testing begins, your team must have been told — as a group, not individually — that service quality and security practices are tested, and that interactions may be recorded.</p>
      <div className="mt-6 bg-manila border border-rule p-5 relative">
        <span className="t-label text-olive">Template language</span>
        <p className="t-body text-base mt-2 leading-snug" data-testid="notice-template">“{TEMPLATE(state.client.legalName)}”</p>
        <button type="button" onClick={() => navigator.clipboard.writeText(TEMPLATE(state.client.legalName))} className="t-label text-carbon hover:text-stamp mt-3" data-testid="copy-template-btn">Copy</button>
      </div>
      <div className="mt-8 flex flex-col gap-6">
        <Check checked={confirmed} onChange={setConfirmed} testId="notice-check">Our workforce has been given this notice, as a group.</Check>
        <div className="max-w-xs"><Field label="Date notice was given" name="date" type="date" value={date} onChange={(e) => setDate(e.target.value)} required testId="notice-date" /></div>
      </div>
      {reveal && <p className="t-body text-base italic text-olive mt-6 max-w-[60ch]" data-testid="hesitation-note">We know this is the step people want to skip. It's the one that makes the rest lawful in a lot of states, and it's what lets us promise your team that nobody gets named.</p>}
      <Err msg={err} />
      <div className="mt-8 flex gap-4 items-center">
        <button onClick={submit} className="btn btn-stamp" disabled={!confirmed || !date} data-testid="step4-submit">Confirm and Continue</button>
        <button onClick={() => { setReveal(true); onBack(); }} className="t-label text-olive hover:text-carbon" data-testid="step4-back">← Previous</button>
      </div>
    </StepShell>
  );
};

export const Step5 = ({ state, onDone }) => {
  const [rec, setRec] = useState(Object.fromEntries(state.locations.map((l) => [l.id || l._id, !!l.recordingAuthorized])));
  const [err, setErr] = useState("");
  const submit = async () => { setErr(""); try { await clientApi().post("/authorize/step/5", { recordingByLocation: rec }); onDone(); } catch (x) { setErr(x.response?.data?.detail || x.message); } };
  return (
    <StepShell n={5} title="Recording authorization">
      <p className="t-body text-lg max-w-[62ch]">Some states require every party to consent before a call is recorded. Where you've given your team notice and you're confident recording is permitted, switch it on. Where you're unsure, leave it off — we work from written notes instead, and the scoring is unaffected.</p>
      <div className="mt-8 border-t border-carbon">
        {state.locations.map((l) => { const id = l.id || l._id; return (
          <label key={id} className="flex items-center justify-between py-4 border-b border-rule cursor-pointer" data-testid={`rec-row-${id}`}>
            <span className="t-body text-lg">{l.name}</span>
            <span className="flex items-center gap-3">
              <span className="t-label text-olive">{rec[id] ? "Recording on" : "Off — notes only"}</span>
              <input type="checkbox" checked={!!rec[id]} onChange={(e) => setRec({ ...rec, [id]: e.target.checked })} className="w-5 h-5 accent-[#1A1A18]" data-testid={`rec-toggle-${id}`} />
            </span>
          </label>); })}
      </div>
      <Err msg={err} />
      <button onClick={submit} className="btn btn-stamp mt-8" data-testid="step5-submit">Save and Continue</button>
    </StepShell>
  );
};

export const Step6 = ({ onDone }) => {
  const [f, setF] = useState({ stopWord: "", emergencyPhone: "", contactName: "", contactHours: "" });
  const [err, setErr] = useState("");
  const set = (e) => setF({ ...f, [e.target.name]: e.target.value });
  const submit = async (e) => { e.preventDefault(); setErr(""); try { const { data } = await clientApi().post("/authorize/step/6", f); onDone(data); } catch (x) { setErr(x.response?.data?.detail || x.message); } };
  return (
    <StepShell n={6} title="Stop-word and emergency contact">
      <form onSubmit={submit} className="flex flex-col gap-8">
        <div className="grid sm:grid-cols-2 gap-6 items-end">
          <Field label="Stop-word (your choice)" name="stopWord" value={f.stopWord} onChange={set} required testId="stop-word" />
          <div className="flex justify-center"><span className="inline-block border-[3px] border-stamp text-stamp px-5 py-3 t-head text-3xl sm:text-4xl min-w-[180px] text-center" style={{ transform: "rotate(-3deg)" }} data-testid="stop-word-preview">{f.stopWord.trim().toUpperCase() || "—"}</span></div>
        </div>
        <Field label="24/7 number that reaches a human" name="emergencyPhone" type="tel" value={f.emergencyPhone} onChange={set} required testId="emergency-phone" />
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="Named client contact" name="contactName" value={f.contactName} onChange={set} required testId="contact-name-6" />
          <Field label="Their hours" name="contactHours" value={f.contactHours} onChange={set} required placeholder="e.g. Mon–Fri 08:00–18:00 ET" testId="contact-hours" />
        </div>
        <Err msg={err} />
        <button className="btn btn-stamp self-start" data-testid="step6-submit">Submit Authorization</button>
      </form>
    </StepShell>
  );
};
